import React, { useState } from 'react'
import styled from 'styled-components'
import {
  getGasPriceDisplay,
  CurrencySymbolImage,
  DeadlinesDisplay,
  DownArrow,
  ConfirmButton,
  Modal,
  Details,
  TokenAmountReceive,
  Amount,
  TokenAmount,
  Symbol,
  DownArrowContainer,
  OutputDisclaimer,
  getExecutionGasPrepayDisplay,
} from '../../common'
import { useEthers, useSendTransaction } from '../../hooks'
import { CurrencyValue } from '../../model'
import { useGasPrice, useSettings } from '../../providers'
import { getBuyTransaction, getSellTransaction } from '../../transactions'
import { getFirstValueWithSlippage, getSecondValueWithSlippage } from './SlippageMath'
import { getSwapFeeDisplay, getPriceImpactDisplay, getSlippageToleranceDisplay } from './getSwapDisplays'
import { EXECUTE_BUY_GAS_LIMIT, EXECUTE_SELL_GAS_LIMIT } from '../../constants'

interface Props {
  controlled: 'first' | 'second'
  firstValue: CurrencyValue
  secondValue: CurrencyValue
  rate?: string
  swapFee?: CurrencyValue
  slippagePercent?: string
  onClose: () => void
  onSubmit: () => void
}

export function ConfirmSwap({
  controlled,
  firstValue,
  secondValue,
  rate,
  swapFee,
  slippagePercent,
  onSubmit,
  onClose,
}: Props) {
  const { account, chainId } = useEthers()
  const sendTransaction = useSendTransaction()
  const gasPrice = useGasPrice()
  const { settings } = useSettings()
  const [sending, setSending] = useState(false)

  const firstControlled = controlled === 'first'
  const firstValueWithSlippage = getFirstValueWithSlippage(controlled, firstValue, settings)
  const secondValueWithSlippage = getSecondValueWithSlippage(controlled, secondValue, settings)

  const swapFeeDisplay = getSwapFeeDisplay(swapFee)
  const priceImpactDisplay = getPriceImpactDisplay(slippagePercent)
  const slippageToleranceDisplay = getSlippageToleranceDisplay(settings.slippageTolerance)
  const gasPriceDisplay = getGasPriceDisplay(gasPrice)
  const executionGasPrepayDisplay = getExecutionGasPrepayDisplay(
    controlled === 'first' ? EXECUTE_SELL_GAS_LIMIT : EXECUTE_BUY_GAS_LIMIT,
    settings,
    gasPrice
  )

  async function confirmSwap() {
    if (!account || !chainId || !gasPrice || !gasPrice.delayGasPrice) {
      return
    }
    setSending(true)
    try {
      await sendTransaction(
        controlled === 'first'
          ? getSellTransaction(
              account,
              chainId,
              firstValue,
              secondValue,
              secondValueWithSlippage,
              gasPrice.delayGasPrice,
              swapFee,
              settings,
              gasPrice.maxGasFeeForEthMax,
              gasPrice.maxGasTipForEthMax
            )
          : getBuyTransaction(
              account,
              chainId,
              firstValue,
              secondValue,
              firstValueWithSlippage,
              gasPrice.delayGasPrice,
              swapFee,
              settings
            )
      )
      setSending(false)
      onSubmit()
    } catch (err) {
      setSending(false)
      if (err instanceof Error) {
        alert(err.message) // TODO: better error handling
      }
    }
  }

  return (
    <Modal title="Confirm Swap" onClose={onClose}>
      <Content>
        <TokenAmount>
          <Amount>{firstValue.format()}</Amount>
          <Symbol>
            <CurrencySymbolImageStyled symbol={firstValue.currency.symbol} />
            {firstValue.currency.symbol}
          </Symbol>
        </TokenAmount>
        {!firstControlled && (
          <OutputDisclaimerStyled>
            This is an estimated amount. You will pay at most{' '}
            <strong>{firstValueWithSlippage.formatWithSymbol()}</strong> or your order will be refunded.
          </OutputDisclaimerStyled>
        )}
        <DownArrowContainer>
          <DownArrow />
        </DownArrowContainer>
        <TokenAmountReceive>
          <Amount>{secondValue.format()}</Amount>
          <Symbol>
            <CurrencySymbolImageStyled symbol={secondValue.currency.symbol} />
            {secondValue.currency.symbol}
          </Symbol>
        </TokenAmountReceive>
        {firstControlled && (
          <OutputDisclaimerStyled>
            This is an estimated amount. You will receive at least{' '}
            <strong>{secondValueWithSlippage.formatWithSymbol()}</strong> or your order will be refunded.
          </OutputDisclaimerStyled>
        )}
        <Details>
          <li>
            <strong>Price</strong>
            <span>{rate}</span>
          </li>
          <li>
            <strong>Fees</strong>
            <span>{swapFeeDisplay}</span>
          </li>
          <li>
            <strong>Price Impact</strong>
            <span>{priceImpactDisplay}</span>
          </li>
          {firstControlled ? (
            <>
              <li>
                <strong>Minimum Received</strong>
                <span>{secondValueWithSlippage.formatWithSymbol()}</span>
              </li>
            </>
          ) : (
            <>
              <li>
                <strong>Maximum Sent</strong>
                <span>{firstValueWithSlippage.formatWithSymbol()}</span>
              </li>
            </>
          )}
          <li>
            <strong>Slippage Tolerance</strong>
            <span>{slippageToleranceDisplay}</span>
          </li>
          <li>
            <strong>Execution Delay</strong>
            <span>Mining + 5 mins</span>
          </li>
          <li>
            <strong>Execution Gas Prepay</strong>
            <span>{executionGasPrepayDisplay}</span>
          </li>
          <li>
            <strong>Default Gas Price</strong>
            <span>{gasPriceDisplay}</span>
          </li>
        </Details>
        <ConfirmButton onClick={confirmSwap} sending={sending} />
        <DeadlinesDisplay />
      </Content>
    </Modal>
  )
}

const Content = styled.div`
  padding: 15px 20px;
`

const CurrencySymbolImageStyled = styled(CurrencySymbolImage)`
  margin-right: 5px;
`

const OutputDisclaimerStyled = styled(OutputDisclaimer)`
  margin-bottom: 20px;
`
